import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { Form, Button, message } from "antd";
import Breadcrumb from "../../components/Breadcrumb";

import JobsConfigurationElement from "../../components/SFTPConnector/SftpJobElements/JobsConfigurationElement";
import ConnectionConfigurationElement from "../../components/SFTPConnector/SftpJobElements/ConnectionConfigurationElement";
import JobParametersConfigurationElement from "../../components/SFTPConnector/SftpJobElements/JobParametersConfigurationElement";
import ServerPathConfigurationElement from "../../components/SFTPConnector/SftpJobElements/ServerPathConfigurationElement";
import FileSelectionConfigurationElement from "../../components/SFTPConnector/SftpJobElements/FileSelectionConfigurationElement";
import FileNameProcessingElement from "../../components/SFTPConnector/SftpJobElements/FileNameProcessingElement";
import IndexExtractionConfigurationElement from "../../components/SFTPConnector/SftpJobElements/IndexExtractionConfigurationElement";
import MappingConfigurationElement from "../../components/SFTPConnector/SftpJobElements/MappingConfigurationElement";
import StatusUpdateElement from "../../components/SFTPConnector/SftpJobElements/StatusUpdateElement";

import { sftpJobSchema } from "../../schema/sftp.schema";
// react hooks
import { useGetHmgProjectLists } from "../../hooks/hmg.hooks";
import {
  useGetProjectHotels,
  useGetProjectIndexes,
} from "../../hooks/project.hooks";
import {
  useGetJobs,
  useGetConnections,
  useCreateSFTPJobData,
  useUpdateSFTPJobData,
} from "../../hooks/sftp.hooks";

const JOB = {
  jobName: "",
  jobName1: undefined,
  managemetGroupId: undefined,
  projectOId: undefined,
  hotelId: null,
  enabled: false,
  processType: "Standard",
  timezone: undefined,
  deleteFilesFromSource: false,
  ignorePreviousStatus: false,
  connections: [],
  isConnectionOverrides: false,
  connectionOverrides: null,
  schedules: [],
  jobParameters: [],
  serverPath: {
    rootPath: "",
    dynamicPathSegments: [],
  },
  fileSelection: {
    getFilesRecursively: false,
    fileExtensions: [],
    dateModified: { dateType: null, dateValue: null },
    fileNameSelectionType: "All",
    fileNameSelectionValue: null,
    fileNameSelectionSegments: [],
    fileNameSelectionSegmentsConcatChar: "_",
    fileNameSelectionSegmentMatchType: "Contains",
  },
  extensionChanges: [],
  fileNameProcessing: {
    filterByFileExtensions: [],
    filterByRegex: "",
    processingRules: [],
  },
  indexExtraction: {
    fileNameSegmentSeparator: "_",
    indexExtractionList: [],
  },
  mapping: {
    hotelIndexName: undefined,
    mappingDataSource: "SMAPI",
    dataPopulationStrategy: "PNA_MYP_STANDARD",
    failOnIndexAutoPopulateError: false,
    uploadUnmappedFiles: true,
    overrideHotelName: true,
    overrideReportName: true,
    overrideBusinessDate: false,
    overrideReportCategory: true,
    overrideSubmissionSchedule: true,
  },
  statusUpdate: {
    notifyServerDirectoryNotFound: true,
    notifyServerDirectoryEmpty: true,
    notifyUnmappedFiles: true,
    destinations: ["QMS"],
  },
};

const AutomatedJobsConfigurationForm = (props) => {
  // Mode Selection
  const [isAddMode, setIsAddMode] = useState(true);
  const [isCloneMode, setIsCloneMode] = useState(false);
  const [IndexTypeList, setIndexTypeList] = useState();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    clearErrors,
    setError,
    formState: { errors, dirtyFields },
  } = useForm({
    resolver: yupResolver(sftpJobSchema),
    criteriaMode: "firstError",
    mode: "onChange",
    defaultValues: {
      job: JOB,
    },
  });

  const JobName = watch("job.jobName");
  const managementGroupId = watch("job.managemetGroupId");
  const projectId = watch("job.projectOId");
  const HotelID = watch("job.hotelId");

  // react query calls

  const { data: HMGProjectData } = useGetHmgProjectLists(managementGroupId, {
    enabled: !!managementGroupId,
  });
  const { data: projectIndexes } = useGetProjectIndexes(projectId, {
    enabled: !!projectId,
  });
  const { data: projectHotels } = useGetProjectHotels(projectId, {
    enabled: !!projectId,
  });
  const {
    isLoading: isLoadingSFTPData,
    data: SFTPJobData,
    refetch: refetchSFTPJobData,
  } = useGetJobs();
  const { isLoading: isLoadingSFTPConnections, data: SFTPConnectionsData } =
    useGetConnections();

  const onEditJobValues = (e, val) => {
    if ((e !== undefined && val?.key) !== undefined) {
      const jobData = SFTPJobData?.find((el) => el._id === val?.key);
      reset({ job: jobData });
      setValue("job.jobName1", jobData?.jobName);
      setValue("job.projectOId", jobData?.projectOId);
      setValue("job.hotelId", jobData?.hotelId);

      setIsAddMode(false);
      clearErrors();
    } else {
      setIsAddMode(true);
      setIsCloneMode(false);
    }
  };

  // value clear function
  const onClearValues = () => {
    setValue("job.projectOId", undefined);
    setValue("job.hotelId", null);
    setValue("job.timezone", undefined);
    setValue("job.indexExtraction.indexExtractionList", [
      {
        indexId: null,
        indexType: null,
        required: false,
        mappingType: null,
      },
    ]);
    setValue("job.mapping.hotelIndexName", undefined);
    setIndexTypeList(undefined);
  };

  const handleCloneJobCheck = (e) => {
    if (e.target.checked === true) {
      setIsCloneMode(e.target.checked);
      setIsAddMode(true);
      // add only fields that need to be cleared when cloning
      setValue("job.managemetGroupId", undefined);
      onClearValues();
    } else {
      setIsCloneMode(false);
      reset({
        job: JOB,
      });
    }
  };

  // when managemet Group Id change
  useEffect(() => {
    if (
      watch("job.managemetGroupId") === undefined &&
      dirtyFields?.job?.managemetGroupId === true
    ) {
      onClearValues();
    }
  }, [watch("job.managemetGroupId")]);

  // when hotel Id change, existing timezone clear and add the new time zone automatically base on hotel
  useEffect(() => {
    if (dirtyFields?.job?.hotelId === true) {
      setValue("job.timezone", undefined);
    }
    if (watch("job.hotelId") !== null) {
      const SelectedHotelTimeZone = projectHotels?.find(
        (el) => el._id === HotelID,
      );
      setValue("job.timezone", SelectedHotelTimeZone?.time_zone);
    }
  }, [projectHotels, watch("job.hotelId")]);

  useEffect(() => {
    const jobData = SFTPJobData?.find((el) => el.jobName === JobName);
    if (
      isAddMode === false &&
      jobData?.projectOId === undefined &&
      HMGProjectData !== undefined
    ) {
      const SelectedHMGProject = HMGProjectData?.find(
        (el) =>
          el?.hmg_id === jobData?.managemetGroupId &&
          el?.project_id == Number(jobData?.projectId),
      );
      if (SelectedHMGProject !== undefined) {
        setValue("job.projectOId", SelectedHMGProject?._id);
      } else {
        message.error(
          "Project numeric ID not matched with data,please select proper project Id from the list",
        );
      }
    }
  }, [SFTPJobData, HMGProjectData, isAddMode]);

  const SelectedProjectData = HMGProjectData?.find(
    (el) => el._id === projectId,
  );
  const SelectedHotelDataList = projectHotels?.find((el) => el._id === HotelID);

  // sftp job react query call Options
  const QueryOptions = {
    onSuccess: (dataResponse) => {
      message.success(dataResponse.message);
      setIsAddMode(true);
      setIsCloneMode(false);
      refetchSFTPJobData();
    },
    onError: (error) => {
      message.error(error.response.data.message);
    },
  };

  // Create sftp job react query call
  const { mutate: mutateAdd, isSuccess: isSuccessAdd } =
    useCreateSFTPJobData(QueryOptions);
  // Update sftp job react query call
  const { mutate: mutateUpdate, isSuccess: isSuccessUpdate } =
    useUpdateSFTPJobData(QueryOptions);

  const onSubmit = (data) => {
    const jobDataSet = {
      ...data?.job,
    };
    jobDataSet.hotelCode = SelectedHotelDataList?.hotel_code ?? null;
    jobDataSet.hotelName = SelectedHotelDataList?.hotel_name ?? null;
    jobDataSet.entityId = SelectedProjectData?.is_entityid;
    jobDataSet.managemetGroupName = SelectedProjectData?.hmg_name;
    jobDataSet.projectId = SelectedProjectData?.project_id;

    if (isAddMode === true || isCloneMode === true) {
      // for clone mode
      delete jobDataSet._id;
      mutateAdd(jobDataSet);
    } else {
      jobDataSet.jobName = jobDataSet.jobName1;
      mutateUpdate({ id: jobDataSet._id, data: jobDataSet });
    }
  };

  useEffect(() => {
    reset({
      job: JOB,
    });
  }, [isSuccessAdd, isSuccessUpdate]);

  const onError = () => {
    message.error("Please Fill The Necessary Fields");
  };

  return (
    <div>
      <div className="container_aa">
        <h3>
          {isAddMode ? "Add " : "Edit "}
          SFTP Job Configuration
        </h3>
        <Breadcrumb
          routes={[
            { path: "/", breadcrumbName: "Dashboard" },
            {
              path: "/file-transfer/automated-job-configuration-form",
              breadcrumbName: "Job Configuration",
            },
          ]}
        />
      </div>
      <div className="container_ab">
        <div className="container_style_1_b" style={{ width: "100%" }}>
          <form onSubmit={handleSubmit(onSubmit, onError)}>
            <JobsConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
              isAddMode={isAddMode}
              SFTPJobData={SFTPJobData}
              onEditJobValues={onEditJobValues}
              isCloneMode={isCloneMode}
              setError={setError}
              isLoadingSFTPData={isLoadingSFTPData}
              setValue={setValue}
              HMGProjectData={HMGProjectData}
              projectHotels={projectHotels}
              clearErrors={clearErrors}
              handleCloneJobCheck={handleCloneJobCheck}
            />
            <ConnectionConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
              isLoadingSFTPConnections={isLoadingSFTPConnections}
              SFTPConnectionsData={SFTPConnectionsData}
            />
            <JobParametersConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
            />
            <ServerPathConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
            />
            <FileSelectionConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
              setValue={setValue}
            />
            <FileNameProcessingElement
              control={control}
              errors={errors}
              watch={watch}
            />
            <IndexExtractionConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
              projectIndexes={projectIndexes}
              setIndexTypeList={setIndexTypeList}
              IndexTypeList={IndexTypeList}
              dirtyFields={dirtyFields}
              setValue={setValue}
            />
            <MappingConfigurationElement
              control={control}
              errors={errors}
              watch={watch}
              projectIndexes={projectIndexes}
            />
            <StatusUpdateElement control={control} errors={errors} />
            <Form.Item wrapperCol={{ span: 15, offset: 10 }}>
              <Button type="primary" htmlType="submit">
                {isAddMode ? "Submit " : "Update"}
              </Button>
            </Form.Item>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AutomatedJobsConfigurationForm;
